/// <reference path="../monaco/ambient.d.ts" />
/**
 * Editor — the workbench's text-editing primitive.
 *
 * A Monaco editor bound to the `WorkspaceStore`: it renders the store's active
 * file, pushes edits back via `store.edit`, and (optionally) lights up the full
 * ArchLang LSP when given a worker factory. It holds no opinion about file
 * navigation, persistence, or the board — those are other primitives over the
 * same store, so this can be omitted or replaced freely.
 *
 * Editing is ALWAYS allowed, even on a readonly source — `readonly` means "not
 * written back", and local experiments are a feature (they surface as dirty in
 * the store, with revert living in the navbar/file primitive). Pass `locked` to
 * hard-disable typing.
 *
 * Lifecycle: one Monaco model per file (keyed by `memfs:///<path>`), each with
 * its change subscription disposed together — and BOTH disposed when the file is
 * removed/renamed (no leak, no stale-buffer resurrection, and the LSP gets a
 * `didClose` via the model's onWillDispose). The LSP bridge is an instance owned
 * by THIS editor (not a module singleton), so multiple compositions on one page
 * don't collide; it's disposed on unmount. Monaco + the glue load lazily so a
 * read-only composition never pays for the editor bundle.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import type * as MonacoNs from "monaco-editor";
import { registerEditorModel, unregisterEditorModel } from "../editApplier.js";
import { registerLspHandle, unregisterLspHandle } from "../lspClient.js";
import { useWorkspace } from "../WorkspaceContext.js";
import {
  useActiveFile,
  useReveal,
  useWorkspaceFiles,
  type WorkspaceStore,
} from "../WorkspaceStore.js";
import type { CreateWorker, LspBridge } from "../monaco/lspBridge.js";
import { subscribeTheme } from "../../theme/index.js";
import { useTranslation } from "../../i18n";

export interface EditorProps {
  readonly className?: string;
  /** Enable the full LSP (completion / hover / rename / diagnostics) using this
   *  worker factory. The LSP is the sole diagnostics source. */
  readonly createLspWorker?: CreateWorker;
  /** Extra files seeded into the LSP worker VFS (e.g. the bundled stdlib at
   *  `/__stdlib__/…`) so type kinds resolve. */
  readonly lspExtras?: ReadonlyMap<string, string>;
  /** Show only Error squiggles — TODO + graph warnings are noise on a quick
   *  sketch (the scratch surface sets this). */
  readonly errorsOnly?: boolean;
  /** Hard-lock editing regardless of the source. Default: always editable. */
  readonly locked?: boolean;
  /** Monaco option overrides merged over the defaults (and re-applied live when
   *  the object changes) — e.g. a host narrowing the gutter on phones. `locked`
   *  still wins over any `readOnly` passed here. */
  readonly options?: MonacoNs.editor.IStandaloneEditorConstructionOptions;
  /** Called once the Monaco editor is created — for hosts that want the raw
   *  instance (e.g. exposing a debug handle for tests). */
  readonly onEditorReady?: (
    editor: MonacoNs.editor.IStandaloneCodeEditor,
    monaco: typeof MonacoNs,
  ) => void;
}

const LANGUAGE_ID = "archlang";

// Model URI is namespaced by the composition id (`wbId`) in the authority slot,
// so two editors on one page editing the same path get DISTINCT Monaco models
// (Monaco keeps one model per URI globally) and the LSP routes each correctly.
function modelUriFor(monaco: typeof MonacoNs, wbId: string, path: string): MonacoNs.Uri {
  return monaco.Uri.from({
    scheme: "memfs",
    authority: wbId,
    path: "/" + path.replace(/^\//, ""),
  });
}

interface EditorBundle {
  monaco: typeof MonacoNs;
  registerArchLanguage: () => void;
  defaultTheme: () => string;
  applyEditorTheme: () => string;
  createLspBridge: () => LspBridge;
  installSnakeEnter: (
    monaco: typeof MonacoNs,
    editor: MonacoNs.editor.IStandaloneCodeEditor,
    languageId: string,
  ) => MonacoNs.IDisposable;
}

async function loadBundle(): Promise<EditorBundle> {
  const [monaco, lang, bridge] = await Promise.all([
    import("monaco-editor/esm/vs/editor/edcore.main") as Promise<typeof MonacoNs>,
    import("../monaco/language.js"),
    import("../monaco/lspBridge.js"),
  ]);
  return {
    monaco,
    registerArchLanguage: lang.registerArchLanguage,
    defaultTheme: lang.defaultTheme,
    applyEditorTheme: lang.applyEditorTheme,
    createLspBridge: bridge.createLspBridge,
    installSnakeEnter: lang.installSnakeEnter,
  };
}

// The Monaco chunk is ~3.7 MB, so its fetch is the likeliest in-app request to
// lose to a flaky network — and a lost fetch used to be terminal AND silent:
// the rejection was logged and nothing else, leaving the host div empty
// forever. The pane then looked "blank but fine" (no gutter, no cursor, no
// message) while the board beside it kept rendering the model from
// already-loaded code, which reads as "the editor lost my file" rather than "a
// download failed". Surface it instead, and offer the one thing that recovers:
// a page reload.
//
// Retrying `import()` in place is NOT that thing. A failed module fetch is
// recorded in the realm's module map, so re-importing the same specifier
// re-serves the cached rejection without touching the network — the retry
// cannot succeed however healthy the network has since become, and a specifier
// Vite rewrites to a hashed chunk URL can't be cache-busted with a query
// either. Reloading the document is what discards the poisoned module map.
// (Proven by the e2e: after the chunk is reachable again an in-place retry
// stays broken; a reload recovers.)

// A live Monaco model plus its change subscription — disposed together.
interface ModelEntry {
  model: MonacoNs.editor.ITextModel;
  sub: MonacoNs.IDisposable;
}

export function Editor(props: EditorProps): ReactElement {
  const { t } = useTranslation("workbench");
  const store = useWorkspace();
  const activeFile = useActiveFile(store);
  const files = useWorkspaceFiles(store);
  const revealReq = useReveal(store);
  // Stable, unique-per-instance composition id (drives model URI namespacing so
  // multiple editors can coexist on one page). `useId` is unique per component
  // instance; sanitize to a valid URI authority.
  const reactId = useId();
  const wbId = useMemo(() => "wb" + reactId.replace(/[^a-zA-Z0-9]/g, ""), [reactId]);

  const hostRef = useRef<HTMLDivElement>(null);
  const [bundle, setBundle] = useState<EditorBundle | null>(null);
  // Set when the editor bundle failed to load — drives the visible failure
  // state below (the alternative is a permanently blank pane).
  const [bundleError, setBundleError] = useState<Error | null>(null);
  // Bumped on a store `reload` (bulk replaceFiles/hydrate, e.g. a demo tab
  // switch) to force a full LSP reseed even when the file paths are unchanged.
  const [reloadGen, setReloadGen] = useState(0);
  const editorRef = useRef<MonacoNs.editor.IStandaloneCodeEditor | null>(null);
  const models = useRef<Map<string, ModelEntry>>(new Map());
  const bridgeRef = useRef<LspBridge | null>(null);
  const readyFired = useRef(false);
  // A reveal requested for a not-yet-active file: applied once that model mounts.
  const pendingReveal = useRef<{ path: string; line: number; column: number; focus: boolean } | null>(null);
  // True while we push a store value into a model — its change event is an echo,
  // NOT a user edit, and must not be written back to the store.
  const applyingRef = useRef(false);
  const storeRef = useRef<WorkspaceStore>(store);
  storeRef.current = store;

  const disposeModel = (path: string): void => {
    const entry = models.current.get(path);
    if (!entry) return;
    // Detach from the editor first so it never holds a disposed model (the
    // model-swap effect then attaches the next active file's model).
    if (editorRef.current?.getModel() === entry.model) {
      editorRef.current.setModel(null);
    }
    unregisterEditorModel(storeRef.current, path, entry.model);
    entry.sub.dispose();
    entry.model.dispose(); // fires onWillDispose → bridge sends LSP didClose
    models.current.delete(path);
  };

  // Load Monaco + glue once (lazy).
  useEffect(() => {
    let cancelled = false;
    loadBundle()
      .then((b) => {
        if (!cancelled) setBundle(b);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[workbench] editor bundle failed to load", err);
        setBundleError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Boot the editor once the bundle + host exist.
  useEffect(() => {
    if (!bundle || !hostRef.current) return;
    bundle.registerArchLanguage();

    const ed = bundle.monaco.editor.create(hostRef.current, {
      theme: bundle.defaultTheme(),
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: 13,
      tabSize: 2,
      padding: { top: 12 },
      scrollBeyondLastLine: false,
      renderLineHighlight: "line",
      smoothScrolling: true,
      fixedOverflowWidgets: true,
      // ArchLang is a structured language with an authoritative LSP. Monaco's
      // word-based suggestions scrape raw identifiers from code/comments/prose
      // with no grammatical awareness and MERGE them into every list — diluting
      // the LSP's ranked, context-aware items with noise (`a`/`an`/`api`/…). The
      // LSP already covers the legitimate "reuse a word" cases (value reuse,
      // `[[…]]`/`@` in descriptions), so turn word-based suggestions off and let
      // the LSP own completion. (Overridable per-surface via props.options.)
      wordBasedSuggestions: "off",
      ...props.options,
      readOnly: props.locked ?? false,
    });
    editorRef.current = ed;

    // Drop Monaco's "iPadShowKeyboard" contribution: on touch / iOS it floats a
    // little keyboard button over the bottom-right of the editor. Tapping the
    // editor already raises the soft keyboard, so the button is pure redundancy
    // — and on a phone it overlaps the code. Disposing the contribution removes
    // its focus listeners + overlay widget so it never mounts. No-op (null) on
    // builds where the contribution isn't bundled.
    ed.getContribution("editor.contrib.iPadShowKeyboard")?.dispose();

    // Enter on a bare `\` leaves the snake run (§4.13.4). Its sibling — Enter on
    // a `\` step continues the run — is declarative, in the language config.
    const snakeEnter = bundle.installSnakeEnter(bundle.monaco, ed, LANGUAGE_ID);

    // Re-derive the editor theme from live CSS tokens when the app theme flips.
    const unsubTheme = subscribeTheme(() => {
      bundle.monaco.editor.setTheme(bundle.applyEditorTheme());
    });

    // Editor → board: emit the current text selection on every cursor change
    // (debounced) so the camera operator can frame the declarations the user
    // selected — and, on a bare cursor move (empty selection ⇒ `cleared`), track
    // the caret's enclosing-module chain for the scope dial / frame-now anchor
    // and the caret-hold.
    let selTimer: ReturnType<typeof setTimeout> | undefined;
    const onSel = ed.onDidChangeCursorSelection((e) => {
      const sel = e.selection;
      const path = storeRef.current.getState().activeFile;
      if (!path) return;
      const cleared = sel.isEmpty();
      // Monaco's start/end are already normalised top-left → bottom-right.
      const startLine = sel.startLineNumber;
      const endLine = sel.endLineNumber;
      const startColumn = sel.startColumn;
      const endColumn = sel.endColumn;
      // Double-click-a-word shape: pointer-sourced, single line, one bare
      // identifier. The operator suppresses framing for these when they name
      // only a referenced module (copying a word shouldn't fly the camera).
      const selText = !cleared ? (ed.getModel()?.getValueInRange(sel) ?? "") : "";
      const wordLike =
        !cleared &&
        e.source === "mouse" &&
        startLine === endLine &&
        // Unicode ident shape (lexer-mirroring) — an ASCII-only class never
        // classified a double-clicked unicode word as word-like (r46).
        /^[\p{ID_Start}_][\p{ID_Continue}]*$/u.test(selText);
      if (selTimer) clearTimeout(selTimer);
      selTimer = setTimeout(() => {
        storeRef.current.emit({
          type: "editorSelection",
          path,
          startLine,
          endLine,
          startColumn,
          endColumn,
          cleared,
          wordLike,
        });
      }, 150);
    });

    const modelsAtMount = models.current;
    return () => {
      unsubTheme();
      snakeEnter.dispose();
      onSel.dispose();
      if (selTimer) clearTimeout(selTimer);
      ed.dispose();
      editorRef.current = null;
      for (const [path, entry] of modelsAtMount.entries()) {
        unregisterEditorModel(storeRef.current, path, entry.model);
        entry.sub.dispose();
        entry.model.dispose();
      }
      modelsAtMount.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle]);

  // Re-apply caller option overrides live (the boot effect runs once) so a host
  // toggling options — e.g. narrowing the gutter when a viewport crosses the
  // mobile breakpoint — takes effect without recreating the editor.
  useEffect(() => {
    if (props.options) editorRef.current?.updateOptions(props.options);
  }, [props.options]);

  // Dispose the model (and its LSP didOpen) when a file is removed or renamed;
  // force an LSP reseed on a bulk reload (paths may be unchanged but content
  // wholesale-swapped — the worker's workspace must reload, not just didChange).
  useEffect(() => {
    return store.on((ev) => {
      if (ev.type === "fileRemoved") disposeModel(ev.path);
      else if (ev.type === "fileRenamed") disposeModel(ev.from);
      else if (ev.type === "reload") setReloadGen((n) => n + 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // LSP bridge lifecycle — one instance per editor, disposed on unmount (or when
  // the worker factory changes). NOT a module singleton, so two compositions on
  // one page each get their own worker.
  useEffect(() => {
    if (!bundle || !props.createLspWorker) return;
    const bridge = bundle.createLspBridge();
    bridgeRef.current = bridge;
    return () => {
      bridge.dispose();
      bridgeRef.current = null;
    };
  }, [bundle, props.createLspWorker]);

  // (Re)seed the LSP worker VFS when the file SET changes (add/remove) or the
  // stdlib extras change — not on every keystroke (the bridge syncs content via
  // didChange). Keyed on the sorted path list.
  const fileKey = [...files.keys()].sort().join("\n");
  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!bundle || !props.createLspWorker || !bridge) return;
    const initial: Record<string, string> = {};
    for (const [p, content] of files) initial[p] = content;
    for (const [p, content] of props.lspExtras ?? []) initial[p] = content;
    let cancelled = false;
    let published: import("../monaco/lspBridge.js").BridgeHandle | null = null;
    let unsubSync: (() => void) | null = null;
    void bridge
      .start(bundle.monaco, props.createLspWorker, wbId, initial, props.errorsOnly)
      .then((handle) => {
        if (cancelled) return;
        // Publish the live handle so non-editor surfaces (details-pane rename)
        // can reach the worker (WYSIWYG plan §6).
        published = handle;
        registerLspHandle(storeRef.current, handle);
        // Closed-file sync (§6 gap): a store edit to a file WITHOUT a live
        // Monaco model never reaches the worker via didChange (never opened) —
        // forward it with merge semantics so a later rename can't compute
        // edits against stale text. Monaco-bound files keep the didChange
        // path (no double-send).
        unsubSync = storeRef.current.on((ev) => {
          if (ev.type !== "fileChanged") return;
          if (models.current.has(ev.path)) return;
          void handle.syncClosedFile(ev.path, ev.content).catch(() => {});
        });
      })
      .catch((err) => {
        // A "disposed" rejection just means the editor unmounted mid-boot — not
        // an error worth logging.
        if (err instanceof Error && /disposed/.test(err.message)) return;
        console.error("[workbench] LSP failed to start", err);
      });
    return () => {
      cancelled = true;
      unsubSync?.();
      if (published) unregisterLspHandle(storeRef.current, published);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle, props.createLspWorker, props.lspExtras, fileKey, reloadGen]);

  // Swap the visible model when the active file changes (create lazily), then
  // apply any pending reveal queued for it.
  useEffect(() => {
    const ed = editorRef.current;
    if (!bundle || !ed || !activeFile) return;
    const uri = modelUriFor(bundle.monaco, wbId, activeFile);
    let entry = models.current.get(activeFile);
    if (!entry) {
      const existing = bundle.monaco.editor.getModel(uri);
      const model =
        existing ??
        bundle.monaco.editor.createModel(files.get(activeFile) ?? "", LANGUAGE_ID, uri);
      // A resurrected (not-yet-disposed) model from a prior life: resync content.
      if (existing && existing.getValue() !== (files.get(activeFile) ?? "")) {
        applyValue(existing, files.get(activeFile) ?? "");
      }
      const sub = model.onDidChangeContent(() => {
        if (applyingRef.current) return;
        storeRef.current.edit(activeFile, model.getValue());
      });
      entry = { model, sub };
      models.current.set(activeFile, entry);
      // Expose the model to the hoisted EditApplier: board/inspector mutations
      // on this file apply as undo-integrated range edits instead of store
      // whole-buffer writes (WYSIWYG plan §5).
      registerEditorModel(storeRef.current, activeFile, model);
    }
    if (ed.getModel() !== entry.model) ed.setModel(entry.model);

    // Fire onEditorReady once a model is attached (never expose a model-less
    // editor — hosts/tests read editor.getModel() right away).
    if (!readyFired.current) {
      readyFired.current = true;
      props.onEditorReady?.(ed, bundle.monaco);
    }

    tryApplyPendingReveal(ed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle, activeFile]);

  // Apply a queued reveal IFF the editor's live model is the one for the
  // reveal's target path. Gating on the actually-mounted model (identity match
  // against the model registry) — not on `activeFile` — is what makes this
  // robust: when a board click opens a brand-new file tab, the store's
  // `activeFile` flips synchronously but Monaco hasn't swapped models yet, so an
  // eager apply would land on the OLD model and be lost. Idempotent: clears the
  // pending reveal once applied, so the listener + the model-swap effect can
  // both call it without double-jumping.
  const tryApplyPendingReveal = useCallback(
    (ed: MonacoNs.editor.IStandaloneCodeEditor): void => {
      const p = pendingReveal.current;
      if (!p) return;
      const entry = models.current.get(p.path);
      if (!entry || ed.getModel() !== entry.model) return;
      pendingReveal.current = null;
      applyReveal(ed, p.line, p.column, p.focus);
    },
    [],
  );

  // Push external store changes (revert / reseed / collab) into the live model.
  // Guarded by inequality so our own edits don't loop, and by applyingRef so the
  // resulting change event isn't reported back.
  useEffect(() => {
    if (!bundle || !activeFile) return;
    const entry = models.current.get(activeFile);
    if (!entry) return;
    const next = files.get(activeFile) ?? "";
    if (entry.model.getValue() === next) return;
    applyValue(entry.model, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle, files, activeFile]);

  function applyValue(model: MonacoNs.editor.ITextModel, value: string): void {
    applyingRef.current = true;
    try {
      model.setValue(value);
    } finally {
      applyingRef.current = false;
    }
  }

  // Keep the readOnly option in sync with `locked`.
  useEffect(() => {
    editorRef.current?.updateOptions({ readOnly: props.locked ?? false });
  }, [props.locked]);

  // Board → editor reveal: jump to a code location, switching files if needed.
  // Driven by the DURABLE `reveal` store slice (not a transient event) so it
  // survives this editor MOUNTING — opening a file from a board tab mounts the
  // editor column on the same tick the reveal is requested, so a fire-once event
  // would be emitted before this component subscribes and be lost. Nonce-gated:
  // each distinct request fires once; a freshly-mounted editor applies the
  // current request once. Queue → switch file → eager-apply if the model is
  // already mounted; otherwise the model-swap effect applies it after mount.
  const lastRevealNonce = useRef(0);
  useEffect(() => {
    if (!revealReq || revealReq.nonce === lastRevealNonce.current) return;
    lastRevealNonce.current = revealReq.nonce;
    pendingReveal.current = {
      path: revealReq.path,
      line: revealReq.line,
      column: revealReq.column,
      focus: revealReq.focus,
    };
    storeRef.current.setActiveFile(revealReq.path);
    const ed = editorRef.current;
    if (ed) tryApplyPendingReveal(ed);
  }, [revealReq, tryApplyPendingReveal]);

  // Legacy transient `reveal` event — same queue→switch→apply path as the
  // durable slice above. Kept for callers that still emit the event (Studio's
  // always-mounted editor); new code should prefer `requestReveal`.
  useEffect(() => {
    return store.on((ev) => {
      if (ev.type !== "reveal") return;
      pendingReveal.current = {
        path: ev.path,
        line: ev.line ?? 1,
        column: ev.column ?? 1,
        focus: ev.focus ?? false,
      };
      storeRef.current.setActiveFile(ev.path);
      const ed = editorRef.current;
      if (ed) tryApplyPendingReveal(ed);
    });
  }, [store, tryApplyPendingReveal]);

  // The editor code never arrived. Say so — the file is safe in the store (the
  // board is still rendering it), this is only the editor's own download.
  if (bundleError && !bundle) {
    return (
      <div
        data-editor-error
        className={props.className}
        style={{ height: "100%", width: "100%" }}
      >
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-slate-300">{t("editorLoadFailed")}</p>
          <p className="max-w-sm text-xs leading-snug text-slate-400">
            {t("editorLoadFailedHint")}
          </p>
          <button
            type="button"
            data-editor-retry
            onClick={() => window.location.reload()}
            className="rounded-md border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700"
          >
            {t("editorLoadFailedReload")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      className={props.className}
      style={{ height: "100%", width: "100%" }}
    />
  );
}

function applyReveal(
  ed: MonacoNs.editor.IStandaloneCodeEditor,
  line: number,
  column: number,
  focus: boolean,
): void {
  ed.revealLineInCenter(line);
  ed.setPosition({ lineNumber: line, column });
  // A plain board click reveals the declaration WITHOUT yanking keyboard focus
  // off the board (so the user can keep clicking nodes). An explicit "take me to
  // this code" jump (e.g. clicking a diagnostic in the issue popover) passes
  // `focus` so the cursor lands ready to edit.
  if (focus) ed.focus();
}
